import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBusiness } from "@/hooks/use-business";
import { isPerBookingBilling, isPerBookingSignupLocked } from "@/lib/billing-model";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { isNativeIosApp } from "@/lib/platform";
import { openBuukdSupportWhatsApp } from "@/lib/support";

interface SubscriptionGateProps {
  children: React.ReactNode;
}

export function SubscriptionGate({ children }: SubscriptionGateProps) {
  const { business, loading } = useBusiness();
  const navigate = useNavigate();
  const location = useLocation();
  const isNativeIos = isNativeIosApp();

  const subscriptionStatus = (business as any)?.subscription_status;
  const isSuspended = subscriptionStatus === "suspended";
  const isGracePeriod = subscriptionStatus === "grace_period";

  // Allow usage on iOS (metrics only) and billing/settings on web when locked/suspended.
  const allowedPaths = isNativeIos
    ? ["/app/settings", "/app/usage"]
    : ["/app/billing", "/app/usage", "/app/settings"];
  const isAllowedPage = allowedPaths.some(path => 
    location.pathname === path || location.pathname.startsWith(path + "/")
  );

  useEffect(() => {
    if (isPerBookingBilling(business)) {
      if (!isPerBookingSignupLocked(business)) return;
      if (!isNativeIos && !loading && !isAllowedPage) {
        navigate("/app/billing", { replace: true });
      }
      return;
    }
    if (!isNativeIos && !loading && isSuspended && !isAllowedPage) {
      navigate("/app/billing", { replace: true });
    }
  }, [business, isNativeIos, loading, isSuspended, isAllowedPage, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isPerBookingBilling(business)) {
    if (isPerBookingSignupLocked(business) && !isAllowedPage) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-subtle-gray">
          <div className="max-w-md w-full mx-4">
            <div className="bg-background border border-border rounded-lg p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {isNativeIos ? "Account Access Needs Attention" : "Activation required"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {isNativeIos ? "Please contact Buukd support" : "Your free setup period has ended"}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                {isNativeIos
                  ? "Please contact Buukd support to pay your one-time activation fee and restore access."
                  : "Pay the one-time activation fee on the Billing page to continue using Buukd. After that, you only pay as customers book online."}
              </p>
              <Button
                onClick={() => {
                  if (isNativeIos) {
                    openBuukdSupportWhatsApp();
                    return;
                  }
                  navigate("/app/billing");
                }}
                className="w-full"
              >
                {isNativeIos ? "Contact support" : "Go to Billing"}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return <>{children}</>;
  }

  // If suspended and not on allowed page, show suspension message
  if (isSuspended && !isAllowedPage) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-subtle-gray">
        <div className="max-w-md w-full mx-4">
          <div className="bg-background border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {isNativeIos ? "Account Access Needs Attention" : "Account Suspended"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {isNativeIos ? "Please contact Buukd support" : "Your subscription payment failed"}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              {isNativeIos
                ? "Your account needs attention. Please contact Buukd support to restore access to your booking system."
                : "Your account has been suspended due to a failed subscription payment. Please update your payment method to restore access to your booking system."}
            </p>
            <Button
              onClick={() => {
                if (isNativeIos) {
                  openBuukdSupportWhatsApp();
                  return;
                }

                navigate("/app/billing");
              }}
              className="w-full"
            >
              {isNativeIos ? "Contact support" : "Resubscribe Now"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Allow access to allowed pages or if subscription is active/grace period
  // Grace period users can still access the app (they just see the banner)
  return <>{children}</>;
}
